import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import Axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import * as z from "zod";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../../components/Buttons/Buttons";
import { DatePicker } from "../../../../../../components/DatePicker/DatePicker";
import { ErrorPlaceholder } from "../../../../../../components/Error";
import { LinkAttributeField } from "../../../../../../components/LinkAttributeField/LinkAttributeField";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import { SelectBoxV2 } from "../../../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import { endpoints } from "../../../../../../endpoints";
import { Form } from "../../../../../../layout/FormLayout";
import type {
  Language,
  OptionType,
  SupportedLanguage,
} from "../../../../../../types/types";
import type {
  Assets,
  ListDetailsSchema,
  ListItemObjectSchema,
  SupportedAssetCategoryType,
  SupportedAssetType,
} from "../../../../../../types/types.PIM";
import {
  useCountriesList,
  useRegionsList,
} from "../../../../../../util/Locations";
import { getCountryOption } from "../../../../../../util/location";
import { stringIsNotOnlyWhiteSpaceRegex } from "../../../../../../util/regexes";
import {
  getDateTime,
  isAxiosError,
  toTitleCase,
  useFormWrapper,
  useStoreState,
} from "../../../../../../util/util";
import { useAssetHostData } from "../../../../../../util/hooks";
import {
  zodOptionalString,
  zodSelectBoxDefault,
  zodSelectBoxType,
} from "../../../../../../util/zod.util";
import type { IAssetPayload } from "../../../../../SharedPages/ProductDetailPage/Assets/NewAssetForm";
import type { AssetCategory, AssetType } from "../util/AssetsUtil";
import { FileUploader } from "../util/FileUploader";
import {
  AssetMetadataHeading,
  AssetSourceModeController,
  assetMetadataLabelMap,
  assetSourceModeSchema,
  externalAssetInferenceSchema,
  externalAssetValidationSchema,
  getAssetName,
} from "../../../../../SharedPages/ProductDetailPage/Assets/util";

/**
 * This is used for type inference
 */
const AddAssetSchema = z.object({
  asset_name: z.string(),
  asset_source_mode: assetSourceModeSchema,
  external_asset: externalAssetInferenceSchema,
  input_asset_type: zodSelectBoxType,
  input_category_type: zodSelectBoxType,
  asset_host: zodSelectBoxType.optional().nullable(),
  input_lang_type: zodSelectBoxType.optional().nullable(),
  input_document_type: zodSelectBoxType.optional().nullable(),
  file_type: z.instanceof(File).optional(),
  input_issue_date: zodOptionalString,
  input_expiration_date: zodOptionalString,
  input_renewal_date: zodOptionalString,
  input_country: zodSelectBoxType.optional().nullable(),
  input_region: zodSelectBoxType.optional().nullable(),
});

type FormValues = z.infer<typeof AddAssetSchema>;

/**
 *
 * This is used for schema validation
 */
const AddAssetSchemaFn = (t: (s: string) => string) =>
  z
    .object({
      asset_name: z
        .string()
        .regex(stringIsNotOnlyWhiteSpaceRegex, t("This is a required field")),
      asset_source_mode: assetSourceModeSchema,
      external_asset: externalAssetValidationSchema(t),
      input_asset_type: zodSelectBoxDefault(t),
      input_category_type: zodSelectBoxDefault(t),
      asset_host: zodSelectBoxDefault(t).optional().nullable(),
      input_lang_type: zodSelectBoxDefault(t).optional().nullable(),
      file_type: z.instanceof(File).optional().nullable(),
      input_issue_date: zodOptionalString,
      input_expiration_date: zodOptionalString,
      input_renewal_date: zodOptionalString,
      input_country: zodSelectBoxDefault(t).optional().nullable(),
      input_region: zodSelectBoxDefault(t).optional().nullable(),
      input_document_type: zodSelectBoxDefault(t).optional().nullable(),
    })
    .superRefine(({ input_asset_type, input_category_type }, ctx) => {
      if (!input_asset_type?.value || !input_category_type?.value) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("This is a required field"),
          path: ["input_asset_type"],
        });
      }
    });

export const EditAsset = ({
  onComplete,
  refreshAssetsList,
  assetData,
}: {
  onComplete: () => void;
  refreshAssetsList: any;
  assetData: Assets;
}) => {
  const { t } = useTranslation();
  const countries = useCountriesList();
  const regions = useRegionsList();
  const [submitting, setSubmitting] = useState(false);

  const { notifySuccess, notifyError } = useContext(Notifications);
  const {
    tenant_id,
    storefront_id,
    storefront_metadata: { enable_external_assets },
  } = useStoreState();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [assetTypes, setAssetTypes] = useState<
    OptionType<SupportedAssetType>[]
  >([]);
  const [assetCategories, setAssetCategories] = useState<
    OptionType<SupportedAssetCategoryType>[]
  >([]);

  const [assetHostFilters, setAssetHostFilters] = useState<
    OptionType<string>[]
  >([]);

  const [documentTypesFilters, setDocumentTypesFilters] = useState<
    OptionType<string>[]
  >([]);
  const [assetLanguages, setAssetLanguages] = useState<
    OptionType<SupportedLanguage>[]
  >([]);
  const {
    category,
    asset_type,
    language,
    id,
    name,
    file_name,
    issue_date,
    expiration_date,
    renewal_date,
    document_type,
    asset_mode,
    country,
    region,
    host,
    signed_url,
  } = assetData;

  const { assetsHostResponse, assetHostDefault } = useAssetHostData(tenant_id);

  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddAssetSchemaFn(t)),
    defaultValues: {
      asset_source_mode: asset_mode,
      external_asset:
        asset_mode === "external"
          ? { display_text: name, url: signed_url ?? "" }
          : { display_text: "", url: "" },
      input_asset_type: { label: toTitleCase(asset_type), value: asset_type },
      input_category_type: {
        label: category?.name,
        value: category?.id,
      },
      input_lang_type: assetLanguages.find((item) => item.value === language),
      input_document_type: document_type
        ? {
            label: toTitleCase(document_type),
            value: document_type,
          }
        : undefined,
      asset_name: name,
      input_issue_date: issue_date
        ? getDateTime(issue_date, true).dateFullYear
        : undefined,
      input_expiration_date: expiration_date
        ? getDateTime(expiration_date, true).dateFullYear
        : undefined,
      input_renewal_date: renewal_date
        ? getDateTime(renewal_date, true).dateFullYear
        : undefined,
      input_country: country ? getCountryOption(countries, country) : undefined,
      input_region: region
        ? {
            label: region,
            value: region,
          }
        : undefined,
    },
  });
  const {
    handleSubmit,
    control,
    errors,
    formState,
    register,
    setValue,
    watch,
  } = methodsOfUseForm;

  const selectedAssetType: string = watch("input_asset_type")?.value;

  const existingFileName: string = selectedFile
    ? selectedFile.name
    : file_name ?? name;

  const { error: assetTypeError } = useSWR<AssetType>(
    endpoints.v2_storefronts_id_pim_asset_types(),
    {
      onSuccess: ({ asset_types }) =>
        setAssetTypes(
          asset_types.map((type) => ({
            label: toTitleCase(type),
            value: type,
          }))
        ),
    }
  );

  const { error: categoryTypeError } = useSWR<AssetCategory>(
    selectedAssetType
      ? `${endpoints.v2_tenants_id_pim_assets_categories(
          tenant_id
        )}?asset_type=${selectedAssetType}`
      : endpoints.v2_tenants_id_pim_assets_categories(tenant_id),
    {
      onSuccess: ({ asset_categories }) =>
        setAssetCategories(
          asset_categories.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        ),
    }
  );

  const { data: languagesResponse, error: LanguageError } = useSWR<{
    languages: Language[];
  }>(endpoints.v1_storefronts_id_languages(storefront_id), {
    onSuccess: ({ languages }) => {
      setAssetLanguages(
        languages.map((item) => ({
          label: `${item.name} (${item.alpha_2.toUpperCase()})`,
          value: item.alpha_2,
        }))
      );
      const selectLanguageOption = languages.find(
        (item) => item.alpha_2 === language
      );
      setValue(
        "input_lang_type",
        selectLanguageOption?.alpha_2
          ? {
              label: `${
                selectLanguageOption?.name
              } (${selectLanguageOption?.alpha_2.toLocaleUpperCase()})`,
              value: selectLanguageOption?.alpha_2,
            }
          : undefined
      );
    },
    onError: () =>
      notifyError(t("could not load asset languages, Something went wrong.")),
  });

  const { data: documentTypesResponse, error: documentTypeError } = useSWR<{
    data: ListDetailsSchema;
  }>(
    endpoints.v2_tenants_id_pim_get_list_by_name(tenant_id, "Document Types"),
    {
      onError: (error) => {
        console.error("There was an error fetching the Document types list.", {
          error,
        });
      },
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
    }
  );

  const onSubmit = async ({
    asset_name,
    asset_source_mode,
    asset_host,
    external_asset,
    input_asset_type,
    input_category_type,
    input_lang_type,
    input_document_type,
    file_type,
    input_issue_date,
    input_expiration_date,
    input_renewal_date,
    input_region,
    input_country,
  }: FormValues): Promise<void> => {
    const assetName = getAssetName(
      asset_source_mode,
      asset_name,
      external_asset
    );

    const paramsObj: IAssetPayload = {
      name: assetName,
      asset_type: input_asset_type?.value.toLowerCase(),
      category_id: input_category_type?.value,
      asset_mode: asset_source_mode,
      language: input_lang_type?.value,
      issue_date: input_issue_date
        ? getDateTime(input_issue_date).dateFullYear
        : null,
      expiration_date: input_expiration_date
        ? getDateTime(input_expiration_date).dateFullYear
        : null,
      renewal_date: input_renewal_date
        ? getDateTime(input_renewal_date).dateFullYear
        : null,
      document_type:
        !input_document_type?.value || input_document_type?.value === "--"
          ? null
          : input_document_type?.value,
      country: input_country?.value || null,
      region:
        !input_region?.value || input_region?.value === "--"
          ? null
          : input_region?.value,
      asset_host_id: asset_host?.value || "",
    };

    if (asset_source_mode === "external") {
      paramsObj.fully_external_url = external_asset?.url?.trim() || null;
    }

    const baseURL = endpoints.v2_tenants_id_pim_assets_id(tenant_id, id);

    setSubmitting(true);

    try {
      await Axios.patch(baseURL, paramsObj);

      if (asset_source_mode === "gcs" && file_type) {
        const formData = new FormData();
        formData.append("file", file_type);
        await Axios.put(baseURL, formData);
      }
      await refreshAssetsList();
      notifySuccess(t("Asset successfully edited"));
      onComplete();
    } catch (error) {
      if (
        isAxiosError(error) &&
        (error as AxiosError)?.response?.data?.message
      ) {
        notifyError(error?.response?.data?.message);
      } else {
        notifyError(t("could not edit asset, Something went wrong."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (languagesResponse) {
      setAssetLanguages(
        languagesResponse.languages.map((item) => ({
          label: `${item.name} (${item.alpha_2.toUpperCase()})`,
          value: item.alpha_2,
        }))
      );
      const selectLanguageOption = languagesResponse.languages.find(
        (item) => item.alpha_2 === language?.toLocaleLowerCase()
      );
      setValue(
        "input_lang_type",
        selectLanguageOption?.alpha_2
          ? {
              label: `${
                selectLanguageOption?.name
              } (${selectLanguageOption?.alpha_2.toLocaleUpperCase()})`,
              value: selectLanguageOption?.alpha_2,
            }
          : undefined
      );
    }
  }, [languagesResponse, language, setValue]);

  useEffect(() => {
    if (documentTypesResponse) {
      setDocumentTypesFilters(
        (documentTypesResponse.data?.items ?? []).map(
          (type: ListItemObjectSchema) => {
            return { label: type.name, value: type.name };
          }
        )
      );
    }
  }, [documentTypesResponse]);

  useEffect(() => {
    if (assetsHostResponse?.data) {
      setAssetHostFilters(
        assetsHostResponse.data.items.map((type: ListItemObjectSchema) => {
          return { label: type.name, value: type.id };
        })
      );
    }
  }, [assetsHostResponse]);

  useEffect(() => {
    if (countries && country) {
      setValue("input_country", getCountryOption(countries, country));
    }
  }, [countries, country, setValue]);

  if (assetTypeError || categoryTypeError) {
    return (
      <ErrorPlaceholder
        message={t(
          "There was an error loading asset types and categories. Please try again later"
        )}
      />
    );
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SelectBoxV2}
          control={control}
          name="input_asset_type"
          autoComplete={"input_asset_type"}
          placeholder={assetMetadataLabelMap(t).asset_type}
          options={assetTypes}
          errors={errors}
          formState={formState}
          isDisabled={true}
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="input_category_type"
          autoComplete={"input_category_type"}
          placeholder={assetMetadataLabelMap(t).asset_category}
          options={assetCategories}
          errors={errors}
          formState={formState}
        />
        <AssetSourceModeController
          control={control}
          register={register}
          is_edit_mode={true}
        />
        {asset_mode === "external" ? (
          <>
            <LinkAttributeField
              name="external_asset"
              label={t("Asset")}
              displayTextLabel={t("Asset Name")}
              defaultValue={
                asset_mode === "external"
                  ? { display_text: name, url: signed_url ?? "" }
                  : { display_text: "", url: "" }
              }
              urlLabel={t("URL")}
              control={control}
              register={register}
              errors={errors}
              formState={formState}
              isRequired={true}
              updateAssetName={(name) => setValue("asset_name", name)}
            />
            <input ref={register()} type="hidden" name="asset_name" />
          </>
        ) : (
          <>
            <FileUploader
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              setValue={setValue}
              selectedAssetType={selectedAssetType.toLowerCase()}
              control={control}
              errors={errors}
              formState={formState}
              existingFileName={existingFileName}
            />
            <TextField
              name="asset_name"
              label={assetMetadataLabelMap(t).name}
              autoComplete={"asset_name"}
              formState={formState}
              theref={register({
                required: false,
              })}
              errors={errors}
              type="text"
            />
          </>
        )}
        <AssetMetadataHeading />
        {!LanguageError && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="input_lang_type"
            autoComplete={"input_lang_type"}
            placeholder={assetMetadataLabelMap(t).language}
            options={assetLanguages}
            defaultValue={assetLanguages.find(
              (item) => item.value === language
            )}
            errors={errors}
            formState={formState}
          />
        )}
        {assetHostDefault && (
          <>
            {enable_external_assets ? (
              <Controller
                as={SelectBoxV2}
                control={control}
                name="asset_host"
                defaultValue={
                  host ? { label: host.name, value: host.id } : assetHostDefault
                }
                autoComplete={"asset_host"}
                placeholder={assetMetadataLabelMap(t).asset_host}
                options={assetHostFilters}
                rules={{
                  required: true,
                }}
                errors={errors}
                formState={formState}
              />
            ) : (
              <Controller
                name="asset_host"
                defaultValue={assetHostDefault}
                control={control}
                render={() => <input type="hidden" />}
              />
            )}
          </>
        )}
        {!documentTypeError && (
          <Controller
            as={SelectBoxV2}
            control={control}
            isClearable={true}
            name="input_document_type"
            autoComplete={"input_document_type"}
            placeholder={assetMetadataLabelMap(t).document_type}
            options={documentTypesFilters}
            errors={errors}
            formState={formState}
          />
        )}
        <DatePicker
          label={assetMetadataLabelMap(t).issue_date}
          name={"input_issue_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          defaultValue={issue_date ?? undefined}
          placeholder={t("MMM DD, YYYY")}
          isOutsideRange={() => false}
        />

        <DatePicker
          label={assetMetadataLabelMap(t).expiration_date}
          name={"input_expiration_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          placeholder={t("MMM DD, YYYY")}
          defaultValue={expiration_date ?? undefined}
          isOutsideRange={() => false}
        />

        <DatePicker
          label={assetMetadataLabelMap(t).renewal_date}
          name={"input_renewal_date"}
          required={false}
          methodsOfUseForm={methodsOfUseForm}
          placeholder={t("MMM DD, YYYY")}
          defaultValue={renewal_date ?? undefined}
          isOutsideRange={() => false}
        />
        {countries && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="input_country"
            isClearable={true}
            autoComplete={"country"}
            placeholder={assetMetadataLabelMap(t).country}
            defaultValue={
              country ? getCountryOption(countries, country) : undefined
            }
            options={countries}
            rules={{
              required: false,
            }}
            errors={errors}
            formState={formState}
          />
        )}
        <Controller
          as={SelectBoxV2}
          control={control}
          name="input_region"
          isClearable={true}
          autoComplete={"region"}
          placeholder={assetMetadataLabelMap(t).region}
          options={regions}
          rules={{
            required: false,
          }}
          errors={errors}
          formState={formState}
        />
        <ButtonContainer style={{ marginTop: "30px" }}>
          <TertiaryButtonLargeV2
            fullWidth
            disabled={submitting}
            type="button"
            onClick={onComplete}
          >
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2 type="submit" loading={submitting} fullWidth>
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </>
  );
};
